import { io } from "socket.io-client";
import { CHAT_SOCKET_NAMESPACE, CHAT_SOCKET_URL } from "../constants/api";

const ACK_TIMEOUT_MS = 5000;
const CONNECT_TIMEOUT_MS = 15000;

const parseJwtPayload = (token) => {
  try {
    const segment = token.split(".")[1];
    if (!segment) return null;
    const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

const connectionErrorMessage = (payload) => {
  if (typeof payload === "string") return payload;
  if (!payload || typeof payload !== "object") return "Connection error";
  const details = payload.details ? ` (${payload.details})` : "";
  return `${payload.message || "Connection error"}${details}`;
};

const exceptionMessage = (payload) => {
  if (typeof payload === "string" && payload.trim()) return payload.trim();
  if (!payload || typeof payload !== "object") return "Verification over socket failed";
  if (typeof payload.message === "string" && payload.message.trim()) {
    return payload.message.trim();
  }
  return "Verification over socket failed";
};

const isFailedAck = (response) => {
  if (!response || typeof response !== "object") return false;
  if (response.success === false || response.success === 0 || response.success === "false") {
    return true;
  }
  const status = Number(response.status);
  if (Number.isFinite(status) && status >= 400) return true;
  if (response.error) return true;
  if (response.message === "exception") return true;
  return false;
};

/**
 * One-shot verify connection: connect with JWT, emit client:verify, then disconnect.
 * Verification token is sent as header `verificationToken: Bearer {token}` (polling
 * handshake so browsers can attach custom headers).
 * Resolves on Socket.IO ack success, or after emit if no ack/exception arrives in time.
 * Rejects on connect_error / connectionError / exception / failed ack.
 */
export const verifyGuestViaSocket = (socketToken) => {
  return new Promise((resolve, reject) => {
    if (!socketToken) {
      reject(new Error("Missing socket token"));
      return;
    }

    const claims = parseJwtPayload(socketToken) || {};
    const hostId = claims.hostId != null ? String(claims.hostId) : "";
    const qrCode = claims.qrCode ? String(claims.qrCode) : "";
    const unitName = claims.unitName ? String(claims.unitName) : "";
    const guestId = claims.guestId != null ? String(claims.guestId) : "";

    const verificationHeader = `Bearer ${socketToken}`;

    // Connection identity. Verification token is NOT sent in query/params or
    // the client:verify body — only as verificationToken (Bearer …).
    const handshake = {
      token: socketToken,
      verificationToken: verificationHeader,
      ...(hostId ? { hostId } : {}),
      ...(qrCode ? { qrCode } : {}),
      ...(unitName ? { unitName } : {}),
      ...(guestId ? { guestId } : {}),
    };

    const identityQuery = {
      ...(hostId ? { hostId } : {}),
      ...(qrCode ? { qrCode } : {}),
      ...(unitName ? { unitName } : {}),
      ...(guestId ? { guestId } : {}),
    };

    const url = `${CHAT_SOCKET_URL}${CHAT_SOCKET_NAMESPACE}`;
    let settled = false;
    let ackTimer = null;
    let connectTimer = null;

    const socket = io(url, {
      // Polling first so extraHeaders ride on the HTTP handshake (browsers
      // cannot set custom headers on raw WebSocket).
      transports: ["polling", "websocket"],
      autoConnect: true,
      reconnection: false,
      auth: handshake,
      query: identityQuery,
      extraHeaders: {
        verificationToken: verificationHeader,
      },
    });

    const cleanup = () => {
      if (ackTimer) clearTimeout(ackTimer);
      if (connectTimer) clearTimeout(connectTimer);
      socket.removeAllListeners();
      socket.disconnect();
    };

    const finishOk = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve();
    };

    const finishErr = (err) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(err instanceof Error ? err : new Error(String(err)));
    };

    connectTimer = setTimeout(() => {
      finishErr(new Error("Socket connection timed out"));
    }, CONNECT_TIMEOUT_MS);

    socket.on("connect_error", (err) => {
      finishErr(
        err?.message ? new Error(err.message) : new Error("Socket connection failed")
      );
    });

    socket.on("connectionError", (payload) => {
      finishErr(new Error(connectionErrorMessage(payload)));
    });

    socket.on("exception", (payload) => {
      finishErr(new Error(exceptionMessage(payload)));
    });

    socket.on("connect", () => {
      if (connectTimer) {
        clearTimeout(connectTimer);
        connectTimer = null;
      }

      let ackReceived = false;

      // Token is in verificationToken header — do not send in body/params.
      socket.emit("client:verify", {}, (response) => {
        ackReceived = true;
        if (ackTimer) clearTimeout(ackTimer);

        if (isFailedAck(response)) {
          finishErr(
            new Error(exceptionMessage(response) || "Verification over socket failed")
          );
          return;
        }
        finishOk();
      });

      ackTimer = setTimeout(() => {
        if (!ackReceived && !settled) {
          finishOk();
        }
      }, ACK_TIMEOUT_MS);
    });
  });
};
